//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 09/09/2024
// Version       : 1
// Description   : Historial de las revisiones realizadas a un vehiculo
//
//------Changes ----------------------------------------------------------------------------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Dialog,
  DialogContent,
  Chip,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";

// ADJUNTAR ARCHIVOS
import FileUploadTareas from "../FileUploadTareas";

// ICONOS
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardDoubleArrowUpOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowUpOutlined";
import KeyboardDoubleArrowDownOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowDownOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import EventIcon from "@mui/icons-material/Event";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// DAYJS
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localeData from "dayjs/plugin/localeData";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.locale("es");

function HistorialRevision(props) {
  const [fechaActual, setFechaActual] = useState(() =>
    dayjs().subtract(6, "day")
  );

  const [rangoDias, setRangoDias] = useState("Semanal");
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarInactivo, setMostrarInactivo] = useState(false);
  const [ultimaRevision, setUltimaRevision] = useState(null);

  const [openCloseAdj, setOpenCloseAdj] = useState(false); // Estado para abrir/cerrar adjuntos
  const [currentUuid, setCurrentUuid] = useState(null); // UUID Actual

  const obtenerRegistroHistorial = () => {
    setLoading(true);
    const estado = mostrarInactivo ? "" : "1"; // Si mostrarInactivo es true, no se especifica estado, sino se filtra solo estado 1
    const url = `${UrlApi}/vehiculos_revision/historial/${props.vehiculo.id}/estado/${estado}`;

    fetch(url)
      .then((data) => data.json())
      .then((data) => setHistorial(data))
      .catch((error) => console.error("Error al cargar el historial:", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (props.vehiculo) {
      obtenerRegistroHistorial();
    }
  }, [props, mostrarInactivo]);

  const agruparPorParteVehiculo = (historial) => {
    return historial.reduce((grupos, tarea) => {
      const parte = tarea.parte_vehiculo || "Sin especificar"; // Categoría por defecto si falta el dato
      if (!grupos[parte]) {
        grupos[parte] = [];
      }
      grupos[parte].push(tarea);
      return grupos;
    }, {});
  };

  const gruposTareas = agruparPorParteVehiculo(historial);

  // -----------------------------------------------

  const [conteoObservaciones, setConteoObservaciones] = useState({});

  // Obtener la última revisión del vehículo
  const obtenerUltimaRevision = () => {
    fetch(`${UrlApi}/vehiculos_revision/ultima_revision/` + props.vehiculo.id)
      .then((data) => data.json())
      .then((data) => {
        if (data?.length > 0) {
          // Contar observaciones por parte del vehículo
          const conteoObservaciones = data.reduce((acc, tarea) => {
            const parte = tarea.parte_vehiculo || "Sin especificar";
            if (tarea.observacion) {
              acc[parte] = (acc[parte] || 0) + 1; // Incrementar el conteo
            }
            return acc;
          }, {});
          setUltimaRevision(data[0].fecha); // Guardar la fecha de la última revisión
          setConteoObservaciones(conteoObservaciones); // Guardar el conteo
        } else {
          setUltimaRevision(null);
          setConteoObservaciones({});
          console.log("No hay revisiones");
        }
      })
      .catch((error) =>
        console.error("Error obteniendo última revisión:", error)
      );
  };

  useEffect(() => {
    if (props.vehiculo) {
      obtenerUltimaRevision(); // Llamar a la función para obtener la última revisión
    }
  }, [props]);

  // -----------------------------------------------

  const restarDia = () => {
    setFechaActual((prevFecha) => prevFecha.subtract(1, "day"));
  };

  const sumarDia = () => {
    setFechaActual((prevFecha) => {
      const nuevaFecha = prevFecha.add(1, "day");
      return nuevaFecha.isAfter(dayjs()) ? prevFecha : nuevaFecha;
    });
  };

  const reiniciarFecha = () => {
    setFechaActual(dayjs().subtract(6, "day"));
  };

  const irAUltimaRevision = () => {
    if (!ultimaRevision) return;
    const fecha = dayjs(ultimaRevision, "DD-MM-YYYY HH:mm:ss");
    setFechaActual(fecha);
  };

  // -----------------------------------------------

  const obtenerNumeroDeDias = (rango) => {
    switch (rango) {
      case "Semanal":
        return 7;
      case "Mensual":
        return 30;
      case "Quincenal":
      default:
        return 15;
    }
  };

  const generarFechas = (fechaInicio, cantidadDias) => {
    const fechas = [];
    for (let i = 0; i < cantidadDias; i++) {
      const fecha = fechaInicio.add(i, "day");
      if (fecha.isAfter(dayjs())) break;
      fechas.push(fecha.format("DD/MM/YYYY"));
    }
    return fechas;
  };

  const numeroDias = obtenerNumeroDeDias(rangoDias);
  const fechas = generarFechas(fechaActual, numeroDias);

  const handleOpenCloseAdj = (fecha) => {
    // Buscar el UUID correspondiente a la fecha específica
    const registro = historial.find((tarea) => tarea.fecha === fecha);

    if (registro?.uuid_revision) {
      setCurrentUuid(registro.uuid_revision);
      setOpenCloseAdj(true);
    } else {
      console.error("UUID no encontrado para la fecha seleccionada:", fecha);
    }
  };

  // Manejar cambio del checkbox
  const handleMostrarInactivoChange = (event) => {
    setMostrarInactivo(event.target.checked);
  };

  // --------------------------------------------------

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Función para abrir el Drawer
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  // Función para cerrar el Drawer
  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // --------------------------------------------------

  const [estadoAccordion, setEstadoAccordion] = useState({});

  // Función para manejar la expansión individual
  const handleAccordionChange = (parte) => {
    setEstadoAccordion((prev) => ({
      ...prev,
      [parte]: !prev[parte], // Invierte el estado del acordeón específico
    }));
  };

  // Expandir o contraer todos los acordeones
  const toggleExpandirTodos = (expandir) => {
    const nuevoEstado = Object.keys(gruposTareas).reduce((acc, parte) => {
      acc[parte] = expandir; // Establece todos los acordeones al mismo estado
      return acc;
    }, {});
    setEstadoAccordion(nuevoEstado);
  };

  const tareasMostradas = new Set();

  // --------------------------------------------------
  // ADAPTACIÓN A PANTALLA
  // VIEWPORTS
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Número de columnas que se muestran (fechas)
  const cols = fechas.length + 1; // +1 por la columna "Tareas"

  // Calcula ancho estimado por columna
  const widthPerCol = useMemo(() => {
    return (viewportWidth || 1200) / Math.max(1, cols);
  }, [viewportWidth, cols]);

  // reglas/umbrales:
  // >=160px -> full (fecha larga, km, icono)
  // 100-160  -> compact (fecha corta, km, oculto icon)
  // 60-100   -> minimal (fecha dd/MM, oculto km, oculto icon)
  // <60     -> tiny (solo dd, oculto fecha, solo background)
  const flags = useMemo(() => {
    if (widthPerCol >= 160) {
      return {
        mode: "full",
        showAttach: true,
        showKm: true,
        showContent: true,
        dateFmt: "DD/MM/YYYY",
        padding: "6px 10px",
        fontSize: "0.9rem",
      };
    } else if (widthPerCol >= 100) {
      return {
        mode: "compact",
        showAttach: false,
        showKm: true,
        showContent: true,
        dateFmt: "DD/MM",
        padding: "5px 8px",
        fontSize: "0.85rem",
      };
    } else if (widthPerCol >= 60) {
      return {
        mode: "minimal",
        showAttach: false,
        showKm: false,
        showContent: false,
        dateFmt: "DD/MM",
        padding: "4px 6px",
        fontSize: "0.8rem",
      };
    } else {
      return {
        mode: "tiny",
        showAttach: false,
        showKm: false,
        showContent: false,
        dateFmt: "DD",
        padding: "3px 4px",
        fontSize: "0.75rem",
      };
    }
  }, [widthPerCol]);

  return (
    <Dialog fullScreen open={props.open} onClose={props.onClose}>
      <AppBar sx={{ position: "relative", boxShadow: 1, marginBottom: -0.5 }}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Botón para cerrar */}
          <IconButton
            edge="start"
            color="inherit"
            onClick={props.onClose}
            aria-label="close"
            sx={{ mr: 2 }}
          >
            <CloseIcon />
          </IconButton>

          {/* Sección izquierda: título */}
          <Typography variant="h6" fontWeight={600}>
            Historial
          </Typography>

          {/* Sección centro: info vehículo */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            sx={{ mt: 0.5, flex: 1, justifyContent: "center" }}
          >
            <Chip
              label={`Modelo: ${props.vehiculo.modelo || "No disponible"}`}
              size="small"
              variant="outlined"
              sx={{
                color: "#2B76A6",
                bgcolor: "white",
                fontWeight: 500,
                fontSize: 14,
                height: 28,
                "& .MuiChip-label": {
                  px: 2, // padding horizontal
                },
              }}
            />
            <Chip
              label={`Patente: ${props.vehiculo.patente}`}
              size="small"
              variant="outlined"
              sx={{
                color: "#2B76A6",
                bgcolor: "white",
                fontWeight: 500,
                fontSize: 14,
                height: 28,
                "& .MuiChip-label": {
                  px: 2, // padding horizontal
                },
              }}
            />
          </Stack>

          {/* Sección derecha: última revisión */}
          {ultimaRevision ? (
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", whiteSpace: "nowrap" }}
            >
              <b>Última Revisión:</b>{" "}
              {dayjs(ultimaRevision, "DD-MM-YYYY HH:mm:ss").format(
                "DD/MM/YYYY, HH:mm"
              )}{" "}
              ({dayjs(ultimaRevision, "DD-MM-YYYY HH:mm:ss").fromNow()})
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", whiteSpace: "nowrap" }}
            >
              <b>Última Revisión:</b> No hay registros previos
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                position: "fixed",
                bottom: 10, // Distancia desde la parte inferior
                left: "50%", // Centro del Box en el medio horizontal de la pantalla
                transform: "translateX(-50%)", // Box centrado
                width: "90%", // 90% del ancho de la pantalla
                zIndex: 1000, // Superposición sobre otros elementos
                bgcolor: "white",
                boxShadow: 3,
                borderRadius: 1,
                p: 2,
                border: "1px solid gray",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between", // Alinea elementos a los extremos
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Botones para Expandir y Contraer */}
                {historial.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => toggleExpandirTodos(true)}
                      startIcon={<KeyboardDoubleArrowDownOutlinedIcon />}
                    >
                      Expandir
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => toggleExpandirTodos(false)}
                      startIcon={<KeyboardDoubleArrowUpOutlinedIcon />}
                    >
                      Contraer
                    </Button>
                  </Box>
                )}

                {/* Botones para Retroceder, Reiniciar y Avanzar */}
                {historial.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Tooltip title="Retroceder" arrow>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={restarDia}
                      >
                        <ArrowBackIcon />
                      </Button>
                    </Tooltip>

                    <Tooltip title="Ir a Última Revisión" arrow>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={irAUltimaRevision}
                        disabled={!ultimaRevision}
                      >
                        <EventIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Ir a Hoy" arrow>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={reiniciarFecha}
                      >
                        <CalendarTodayIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Avanzar" arrow>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={sumarDia}
                      >
                        <ArrowForwardIcon />
                      </Button>
                    </Tooltip>
                  </Box>
                )}

                {/* OPCIONES */}
                <Box sx={{ display: "flex" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ ml: 2 }}
                    onClick={handleDrawerOpen}
                    endIcon={<SettingsIcon />}
                  >
                    Opciones
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Tabla de Tareas */}
            <Box sx={{ marginBottom: "75px" }}>
              {Object.entries(gruposTareas).map(([parte, tareas]) => (
                <Accordion
                  key={parte}
                  expanded={!!estadoAccordion[parte]} // Lee el estado individual
                  onChange={() => handleAccordionChange(parte)}
                  sx={{
                    padding: 0.3,
                    margin: 0,
                    borderRadius: !!estadoAccordion[parte] ? "6px" : "0px",
                    "&::before": { display: "none" }, // elimina borde predeterminado
                    boxShadow: !!estadoAccordion[parte]
                      ? "0 2px 4px rgba(0,0,0,0.2)"
                      : "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                    aria-controls={`panel-${parte}-content`}
                    id={`panel-${parte}-header`}
                    sx={{
                      bgcolor: "#1976d2",
                      color: "#fff",
                      minHeight: "36px !important", // más delgado
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      },
                      "&:hover": {
                        bgcolor: "#125a9e",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: "100%" }}
                    >
                      {conteoObservaciones[parte] > 0 && (
                        <Chip
                          label={conteoObservaciones[parte]}
                          size="small"
                          sx={{
                            bgcolor: "white",
                            color: "darkblue",
                            fontSize: "0.75rem",
                            height: 20,
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          textAlign: "center",
                          flexGrow: 0,
                        }}
                      >
                        {parte}
                      </Typography>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      p: 0,
                      bgcolor: "#fafafa",
                      // contenedor con scroll y que permite que thead sticky funcione
                      "& > .table-wrapper": {
                        maxHeight: 360,
                        overflowX: "auto",
                        overflowY: "auto",
                        borderRadius: 1,
                        border: "1px solid #e6e6e6",
                      },
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        fontSize: "0.95rem",
                        tableLayout: "fixed",
                      }}
                    >
                      <thead>
                        <tr>
                          {/* Título "Tareas" */}
                          <th
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 800,
                              backgroundColor: "#deefffff",
                              borderBottom: "1px solid #c7cee0ff",
                              padding: "8px 10px",
                              width: 220,
                              textAlign: "center",
                              fontWeight: 700,
                              fontSize: "0.95rem",
                            }}
                          >
                            Tareas
                          </th>

                          {/* Encabezados con las fechas, kilometrajes y adjuntos */}
                          {fechas.map((fecha, fechaIndex) => {
                            const registro = historial.find(
                              (fila) => fila.fecha === fecha
                            );
                            const km = registro?.kilometraje ?? "-";

                            // Parseando con el formato DD/MM/YYYY
                            const dateLabel = dayjs(fecha, "DD/MM/YYYY").format(
                              flags.dateFmt
                            );

                            return (
                              <th
                                key={fechaIndex}
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 500,
                                  backgroundColor: "#deefffff",
                                  borderBottom: "1px solid #c7cee0ff",
                                  padding: flags.padding,
                                  minWidth: Math.max(
                                    80,
                                    Math.floor(widthPerCol)
                                  ),
                                  textAlign: "center",
                                  fontWeight: 700,
                                  fontSize: flags.fontSize,
                                  borderLeft: "1px solid #c7cee0ff",
                                  verticalAlign: "top",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 4,
                                    position: "relative",
                                    minHeight: 28,
                                  }}
                                >
                                  {/* Fecha */}
                                  <div
                                    style={{
                                      fontSize: flags.fontSize,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {dateLabel}
                                  </div>

                                  {/* km (solo si hay espacio suficiente) */}
                                  {flags.showKm && (
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#666",
                                      }}
                                    >
                                      km: {km}
                                    </div>
                                  )}

                                  {/* Icono adjuntos: solo si showAttach */}
                                  {flags.showAttach && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        right: 8,
                                        top: 8,
                                      }}
                                    >
                                      <Tooltip
                                        title="Ver adjuntos"
                                        placement="top"
                                        arrow
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleOpenCloseAdj(fecha)
                                          }
                                          sx={{ p: 0 }}
                                        >
                                          <AttachFileIcon
                                            fontSize="small"
                                            color="primary"
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>

                      <tbody>
                        {tareas.map((tarea) => {
                          if (tareasMostradas.has(tarea.tarea)) return null;
                          tareasMostradas.add(tarea.tarea);

                          return (
                            <tr
                              key={tarea.tarea}
                              style={{ borderTop: "1px solid #f0f0f0" }}
                            >
                              <td
                                style={{
                                  borderBottom: "1px solid #c7cee0ff",
                                  background: "#EAF5FF",
                                  padding: "8px",
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  fontWeight: 500,
                                }}
                              >
                                {tarea.tarea}
                              </td>

                              {fechas.map((fecha, idx) => {
                                const tareaEnFecha = historial.find(
                                  (t) =>
                                    t.tarea === tarea.tarea && t.fecha === fecha
                                );

                                let bg = "#f3f3f3";
                                let content = "";
                                let hasObservation = false;

                                if (!tareaEnFecha) {
                                  bg = "#f3f3f3";
                                } else if (tareaEnFecha.aprobado === 1) {
                                  bg = "#DEFFE2";
                                } else {
                                  bg = "#FFFDDE";
                                  hasObservation = Boolean(
                                    tareaEnFecha.observacion
                                  );
                                  content = flags.showContent
                                    ? tareaEnFecha.observacion || ""
                                    : "";
                                }

                                return (
                                  <td
                                    key={idx}
                                    style={{
                                      position: "relative", // Para el overlay (tooltip)
                                      borderBottom: "1px solid #c7cee0ff",
                                      padding: "6px 8px",
                                      textAlign: "left",
                                      verticalAlign: "top",
                                      maxWidth: 260,
                                      whiteSpace: "pre-wrap",
                                      background: bg,
                                      transition: "background 0.25s ease",
                                      borderLeft: "1px solid #c7cee0ff",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {/* contenido visible o un "spacer" para dar altura */}
                                    {flags.showContent ? (
                                      <div
                                        style={{
                                          fontSize: "0.9rem",
                                          color: tareaEnFecha
                                            ? "#111"
                                            : "#7a7a7a",
                                        }}
                                      >
                                        {content}
                                      </div>
                                    ) : (
                                      <div style={{ minHeight: 24 }} /> // Altura minima cuando no se muestra detalle
                                    )}

                                    {/* Overlay que cubre TODA la celda y dispara el tooltip */}
                                    {!flags.showContent && hasObservation && (
                                      <Tooltip
                                        title={tareaEnFecha.observacion}
                                        arrow
                                        placement="top"
                                        disableInteractive
                                        enterTouchDelay={0}
                                        slotProps={{
                                          popper: {
                                            modifiers: [
                                              {
                                                name: "offset",
                                                options: {
                                                  offset: [0, -14],
                                                },
                                              },
                                            ],
                                          },
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            inset: 0, // top:0 right:0 bottom:0 left:0
                                            cursor: "help",
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}

        {/* OPCIONES (DRAWER) */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerClose} // Cierra el Drawer al hacer clic fuera de él
          disablePortal // Evita que el Drawer se renderice fuera del Dialog
          PaperProps={{
            sx: {
              zIndex: 2000, // Encima de otros modales/dialogs
            },
          }}
        >
          <Box
            sx={{
              width: "top" || "bottom" ? "auto" : 350,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              style={{
                backgroundColor: "#1976D2",
                color: "white",
                padding: "11px",
                fontWeight: "bold",
              }}
            >
              Opciones
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: 250, padding: 2 }}>
              {/* Select de "Rango de Días" */}
              <FormControl fullWidth>
                <InputLabel>Rango de Días</InputLabel>
                <Select
                  label="Rango de Días"
                  size="small"
                  value={rangoDias}
                  onChange={(e) => setRangoDias(e.target.value)}
                >
                  <MenuItem value="Semanal">Semanal</MenuItem>
                  <MenuItem value="Quincenal">Quincenal</MenuItem>
                  <MenuItem value="Mensual">Mensual</MenuItem>
                </Select>
              </FormControl>

              {/* Checkbox para mostrar inactivos */}
              <FormControlLabel
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
                control={
                  <Checkbox
                    checked={mostrarInactivo}
                    onChange={handleMostrarInactivoChange}
                  />
                }
                label="Mostrar Inactivos"
              />
            </Box>
          </Box>
        </Drawer>
      </DialogContent>

      {/* Componente de adjuntos */}
      {openCloseAdj && (
        <FileUploadTareas
          fileId={currentUuid}
          openclose={true}
          mostrar={false}
          onClose={() => setOpenCloseAdj(false)}
        />
      )}
    </Dialog>
  );
}

export default HistorialRevision;
